import { useNavigate } from 'react-router-dom'
import { Stack, Title, Group, Button, Card, Badge, Text } from '@mantine/core'
import { IconArrowLeft } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { nodesApi } from '@/api'
import { formatBytes } from '@/utils/format'

interface NodeDetailProps {
  nodeId: string
}

export function NodeDetail({ nodeId }: NodeDetailProps) {
  const navigate = useNavigate()
  
  const { data: node, isLoading } = useQuery({
    queryKey: ['nodes', nodeId],
    queryFn: async () => {
      const { data } = await nodesApi.get(nodeId)
      return data
    },
  })

  if (isLoading || !node) {
    return <div>Loading...</div>
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={18} />}
            onClick={() => navigate('/nodes')}
          >
            Back
          </Button>
          <Title order={2}>{node.Description.Hostname}</Title>
        </Group>
        <Badge
          color={node.Status.State === 'ready' ? 'green' : 'red'}
          variant="dot"
          size="lg"
        >
          {node.Status.State}
        </Badge>
      </Group>

      <Card shadow="sm" p="lg" radius="md">
        <Text size="lg" fw={600} mb="md">
          Node Details
        </Text>
        <Stack gap="sm">
          <Group>
            <Text fw={500}>Role:</Text>
            <Text c="dimmed">{node.Spec.Role}</Text>
          </Group>
          <Group>
            <Text fw={500}>Availability:</Text>
            <Text c="dimmed">{node.Spec.Availability}</Text>
          </Group>
          <Group>
            <Text fw={500}>Platform:</Text>
            <Text c="dimmed">{node.Description.Platform.OS} {node.Description.Platform.Architecture}</Text>
          </Group>
          <Group>
            <Text fw={500}>Resources:</Text>
            <Text c="dimmed">
              {node.Description.Resources.NanoCPUs / 1_000_000_000} CPUs, {formatBytes(node.Description.Resources.MemoryBytes)} RAM
            </Text>
          </Group>
          {node.Description?.Engine?.EngineVersion && (
            <Group>
              <Text fw={500}>Engine Version:</Text>
              <Text c="dimmed">{node.Description.Engine.EngineVersion}</Text>
            </Group>
          )}
          <Group>
            <Text fw={500}>Address:</Text>
            <Text c="dimmed">{node.Status.Addr}</Text>
          </Group>
        </Stack>
      </Card>
    </Stack>
  )
}